import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  heroData,
  brandPositionData,
  serviceGroups,
  reverseAgingConcept,
  coreValuesPreview,
  agingTypesHub,
  testimonialsPreview,
  finalCtaData,
  lineUrl,
} from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

function useRevealOnScroll() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.1 }
    );
    el.querySelectorAll(".reveal").forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* Animated scan line for hero tech card */
function ScanLine() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-2xl">
      <div
        className="absolute left-0 right-0 h-px bg-neon-cyan/40"
        style={{
          animation: "scanMove 3s linear infinite",
          boxShadow: "0 0 8px rgba(34,211,238,0.6)",
        }}
      />
      <style>{`
        @keyframes scanMove {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/* Tech visual card for hero right side */
function HeroTechCard() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1200);
    return () => clearInterval(id);
  }, []);

  const metrics = [
    { label: "COLLAGEN", value: `${67 + (tick % 5)}%`, color: "#FACC6B" },
    { label: "ELASTICITY", value: `${58 + (tick % 7)}%`, color: "#4BC0FF" },
    { label: "METABOLISM", value: `${72 + (tick % 4)}%`, color: "#22D3EE" },
    { label: "HYDRATION", value: `${81 - (tick % 3)}%`, color: "#35E0B9" },
  ];

  return (
    <div className="relative w-full max-w-md mx-auto lg:mx-0">
      <div className="relative aspect-square rounded-2xl border border-cyber-gold/20 bg-abyss-800/60 backdrop-blur-sm overflow-hidden p-6 md:p-8">
        <ScanLine />

        {/* Circuit corners */}
        <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-cyber-gold/30 rounded-tl-2xl" />
        <div className="absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-cyber-gold/30 rounded-tr-2xl" />
        <div className="absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-cyber-gold/30 rounded-bl-2xl" />
        <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-cyber-gold/30 rounded-br-2xl" />

        {/* Center hex / core */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-32 h-32 md:w-40 md:h-40">
            <div className="absolute inset-0 rounded-full border border-cyber-gold/20 animate-pulse" />
            <div className="absolute inset-3 rounded-full border border-neon-cyan/20" />
            <div className="absolute inset-6 rounded-full border border-cyber-gold/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-cyber-gold/10 border border-cyber-gold/40 flex items-center justify-center">
                <i className="ri-cpu-line text-cyber-gold text-3xl md:text-4xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Orbiting data chips */}
        {metrics.map((m, i) => {
          const angles = [-30, 30, -150, 150];
          const angle = (angles[i] * Math.PI) / 180;
          const radius = 42;
          const x = 50 + radius * Math.cos(angle);
          const y = 50 + radius * Math.sin(angle);
          return (
            <div
              key={m.label}
              className="absolute px-2.5 py-1.5 rounded-lg border font-mono text-base whitespace-nowrap"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transform: "translate(-50%, -50%)",
                borderColor: `${m.color}30`,
                color: m.color,
                background: `${m.color}08`,
              }}
            >
              <span className="opacity-70">{m.label}</span>{" "}
              <span className="font-bold">{m.value}</span>
            </div>
          );
        })}

        {/* Bottom status bar */}
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-base text-ghost-dim">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-neon-cyan animate-pulse" />
            AGE_CODE: SCANNING
          </span>
          <span className="text-cyber-gold/60">v2.0.26</span>
        </div>
      </div>

      {/* Floating badges outside card */}
      <div className="absolute -bottom-3 -left-3 card-cyber px-4 py-2">
        <span className="font-mono text-neon-cyan text-base">15+ YRS</span>
        <span className="text-ghost text-base ml-2">臨床經驗</span>
      </div>
      <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full border border-cyber-gold/20 flex items-center justify-center bg-abyss-800/80 backdrop-blur-sm">
        <i className="ri-shield-keyhole-line text-cyber-gold text-xl" />
      </div>
    </div>
  );
}

/* Auto-rotating hero background slideshow */
const heroSlides = [
  {
    img: "https://readdy.ai/api/search-image?query=futuristic%20holographic%20health%20monitoring%20interface%20with%20golden%20cyan%20glowing%20data%20streams%20flowing%20through%20dark%20void%2C%20abstract%20medical%20technology%20visualization%2C%20deep%20navy%20black%20background%20with%20circuit%20patterns%2C%20elegant%20minimal%20composition%2C%20cinematic%20lighting&width=1920&height=1080&seq=hero-slide-1&orientation=landscape",
    alt: "逆齡科技視覺背景",
  },
  {
    img: "https://readdy.ai/api/search-image?query=abstract%20cellular%20DNA%20helix%20glowing%20with%20golden%20and%20teal%20bioluminescent%20light%20against%20deep%20dark%20background%2C%20microscopic%20beauty%20of%20human%20biology%2C%20futuristic%20biotechnology%20art%2C%20elegant%20scientific%20visualization%20with%20flowing%20particles&width=1920&height=1080&seq=hero-slide-2&orientation=landscape",
    alt: "生物科技背景",
  },
  {
    img: "https://readdy.ai/api/search-image?query=elegant%20abstract%20golden%20light%20rays%20penetrating%20through%20dark%20misty%20atmosphere%2C%20futuristic%20wellness%20sanctuary%20ambiance%20with%20subtle%20cyan%20and%20gold%20ambient%20glow%2C%20cinematic%20luxury%20health%20spa%20atmosphere%2C%20dark%20moody%20background%20with%20ethereal%20light&width=1920&height=1080&seq=hero-slide-3&orientation=landscape",
    alt: "逆齡光環背景",
  },
];

function HeroBackgroundSlideshow() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {heroSlides.map((slide, i) => (
        <div
          key={slide.img}
          className="absolute inset-0 transition-opacity duration-[2000ms] ease-in-out"
          style={{ opacity: i === index ? 1 : 0 }}
        >
          <img
            src={slide.img}
            alt={slide.alt}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-abyss-900/60 via-abyss-900/50 to-abyss-900/80" />
      <div className="absolute inset-0 circuit-bg opacity-20" />
    </div>
  );
}

export default function Home() {
  const revealRef = useRevealOnScroll();

  return (
    <>
      <SEO
        title="時光逆行駭客｜曾驛翔院長｜醫學美容逆齡科技美學"
        description="老化，是一段可以被駭入的程式。曾驛翔院長帶領時光逆行駭客團隊，以AI美學設計與醫學美容科技，為您設計專屬逆齡路線圖。老化程式除錯，讀懂身體狀態，開啟您的逆齡未來。"
        keywords="醫學美容,逆齡,曾驛翔,時光逆行駭客,醫美,抗衰老,AI美學,皮膚科,功能性醫療"
        canonicalPath="/"
        schema={{
          "@context": "https://schema.org",
          "@type": "Physician",
          "name": "時光逆行駭客｜曾驛翔院長",
          "description":
            "曾驛翔院長帶領時光逆行駭客團隊，以AI美學設計與醫學美容科技，提供專屬逆齡路線圖。",
          "url": import.meta.env.VITE_SITE_URL
            ? `${import.meta.env.VITE_SITE_URL}/`
            : "",
          "areaServed": "台北市",
          "medicalSpecialty": "醫學美容",
          "founder": {
            "@type": "Person",
            "name": "曾驛翔",
            "jobTitle": "院長",
            "description": "皮膚科背景｜醫學美容・功能性醫療整合",
          },
        }}
      />
    <div className="bg-abyss-900" ref={revealRef}>
      {/* ===== HERO ===== */}
      <section className="relative min-h-[100dvh] flex items-center overflow-hidden">
        <HeroBackgroundSlideshow />
        {/* Decorative circuit pattern SVG */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-10" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="circuit" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M0 50h40M60 50h40M50 0v40M50 60v40" stroke="#FACC6B" strokeWidth="0.5" fill="none" />
              <circle cx="50" cy="50" r="2" fill="#FACC6B" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#circuit)" />
        </svg>

        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 py-24 pt-32 md:pt-0 w-full" style={{ maxWidth: 1280 }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left: Text */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <span className="inline-block font-mono text-neon-cyan text-base tracking-widest mb-4 md:mb-6 reveal opacity-0 translate-y-6 transition-all duration-700">
                {heroData.tagline}
              </span>
              <h1 className="text-[2rem] leading-[1.3] md:text-[2.75rem] md:leading-[1.25] font-bold text-ghost reveal opacity-0 translate-y-6 transition-all duration-700 delay-100">
                {heroData.titleLine1}
              </h1>
              <h1 className="text-[2rem] leading-[1.3] md:text-[2.75rem] md:leading-[1.25] font-bold text-gradient-gold mt-1 reveal opacity-0 translate-y-6 transition-all duration-700 delay-150">
                {heroData.titleLine2}
              </h1>
              <p className="mt-5 md:mt-6 text-ghost-muted text-[1.1875rem] leading-[1.75] max-w-xl mx-auto lg:mx-0 reveal opacity-0 translate-y-6 transition-all duration-700 delay-200">
                {heroData.subtitle}
              </p>
              <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start reveal opacity-0 translate-y-6 transition-all duration-700 delay-300">
                <a
                  href={lineUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-line text-[1.0625rem]"
                >
                  <i className="ri-line-fill" />
                  {heroData.ctaLine}
                </a>
                <Link to="/booking" className="btn-gold text-[1.0625rem]">
                  {heroData.ctaBooking}
                </Link>
              </div>
            </div>

            {/* Right: Tech Visual */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end reveal opacity-0 translate-y-6 transition-all duration-700 delay-400">
              <HeroTechCard />
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-neon-blue/60 animate-float">
          <span className="font-mono text-base tracking-widest">{heroData.scrollHint}</span>
          <i className="ri-arrow-down-line text-base" />
        </div>
      </section>

      {/* ===== BRAND POSITION ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center max-w-4xl mx-auto reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">// BRAND POSITIONING</span>
            <h2 className="mt-5 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {brandPositionData.title}
            </h2>
            <p className="mt-6 text-ghost-muted text-[1.1875rem] leading-[1.75]">
              {brandPositionData.description}
            </p>
          </div>
        </div>
      </section>

      {/* ===== FOUR SERVICE GROUPS ===== */}
      <section className="py-20 md:py-28 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">// TARGET AUDIENCE</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              四種族群，一種態度
            </h2>
            <p className="mt-4 text-ghost-muted text-[1.1875rem] leading-[1.75]">
              無論你處於哪個階段，老化都有它的路徑。我們為不同族群設計專屬逆齡方案。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceGroups.map((group, idx) => (
              <div
                key={group.id}
                className="group card-cyber p-6 md:p-8 reveal opacity-0 translate-y-6 transition-all duration-700 hover:-translate-y-2"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110"
                  style={{ background: `${group.accent}12`, border: `1px solid ${group.accent}25` }}
                >
                  <i
                    className={`${group.icon} text-2xl transition-transform duration-500 group-hover:scale-110`}
                    style={{ color: group.accent }}
                  />
                </div>
                <h3 className="text-[1.25rem] leading-[1.5] md:text-[1.625rem] md:leading-[1.45] font-semibold text-ghost mb-4 group-hover:text-cyber-gold transition-colors duration-500">
                  {group.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {group.items.map((item) => (
                    <span
                      key={item}
                      className="px-3 py-1.5 rounded-lg text-base font-medium transition-all duration-500 group-hover:scale-[1.03]"
                      style={{
                        background: `${group.accent}10`,
                        color: group.accent,
                        border: `1px solid ${group.accent}20`,
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-1 text-base opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500" style={{ color: group.accent }}>
                  <span className="font-medium">探索方案</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== REVERSE AGING CONCEPT ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 group">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-cyber-gold/20 transition-all duration-500 group-hover:border-cyber-gold/40 group-hover:shadow-lg group-hover:shadow-cyber-gold/10">
                <img
                  src="https://readdy.ai/api/search-image?query=futuristic%20medical%20assessment%20interface%20holographic%20display%20with%20facial%20data%20analysis%20graphs%20and%20biometric%20charts%2C%20dark%20blue%20black%20background%20with%20gold%20cyan%20accents%2C%20high-tech%20medical%20concept%20art%2C%20clean%20UI%20design%2C%20no%20human%20face%2C%20abstract%20data%20visualization&width=700&height=525&seq=assessment-ui&orientation=landscape"
                  alt="逆齡評估介面"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-abyss-900/60 via-transparent to-transparent" />
              </div>
            </div>
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
              <span className="section-label">// ASSESSMENT FIRST</span>
              <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
                {reverseAgingConcept.title}
              </h2>
              <p className="mt-5 text-ghost-muted text-[1.1875rem] leading-[1.75]">
                {reverseAgingConcept.description}
              </p>
              <div className="mt-8 flex flex-col gap-4">
                {reverseAgingConcept.evaluations.map((ev, idx) => (
                  <div
                    key={idx}
                    className="group/card p-5 rounded-xl bg-abyss-700/50 border border-cyber-gold/10 hover:border-cyber-gold/30 hover:-translate-y-1 hover:shadow-md hover:shadow-cyber-gold/5 transition-all duration-500 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-8 h-8 rounded-lg bg-cyber-gold/10 border border-cyber-gold/20 flex items-center justify-center text-cyber-gold font-mono text-base font-bold transition-all duration-500 group-hover/card:scale-110 group-hover/card:bg-cyber-gold/20">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <h3 className="text-[1.125rem] md:text-[1.25rem] font-semibold text-ghost group-hover/card:text-cyber-gold transition-colors duration-500">
                        {ev.label}
                      </h3>
                    </div>
                    <p className="text-ghost-muted text-[1.0625rem] leading-[1.7] pl-11">
                      {ev.desc}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/booking" className="btn-gold text-[1.0625rem]">
                  預約逆齡評估 <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CORE VALUES PREVIEW ===== */}
      <section className="py-20 md:py-28 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">// CORE VALUES</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              三個堅持，一種信念
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {coreValuesPreview.map((val, idx) => (
              <div
                key={idx}
                className="group card-cyber p-6 md:p-8 reveal opacity-0 translate-y-6 transition-all duration-700 hover:-translate-y-2 hover:border-cyber-gold/40 hover:shadow-lg hover:shadow-cyber-gold/10"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="w-14 h-14 rounded-2xl bg-cyber-gold/10 border border-cyber-gold/20 flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110">
                  <i className={`${val.icon} text-cyber-gold text-2xl transition-transform duration-500 group-hover:scale-110`} />
                </div>
                <h3 className="text-[1.25rem] leading-[1.5] md:text-[1.625rem] md:leading-[1.45] font-semibold text-ghost mb-3 group-hover:text-cyber-gold transition-colors duration-500">
                  {val.title}
                </h3>
                <p className="text-ghost-muted text-[1.0625rem] leading-[1.7]">{val.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== KNOWLEDGE HUB ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">// KNOWLEDGE BASE</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {agingTypesHub.title}
            </h2>
            <p className="mt-4 text-ghost-muted text-[1.1875rem] leading-[1.75] max-w-2xl mx-auto">
              {agingTypesHub.description}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {agingTypesHub.types.map((type, idx) => (
              <Link
                key={idx}
                to={type.path}
                className="group card-cyber p-5 md:p-6 text-center reveal opacity-0 translate-y-6 transition-all duration-700 hover:-translate-y-1 hover:border-cyber-gold/40 hover:shadow-lg hover:shadow-neon-cyan/5"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div className="w-14 h-14 rounded-2xl bg-neon-cyan/10 border border-neon-cyan/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-neon-cyan/20 transition-all duration-500 group-hover:scale-110">
                  <i className={`${type.icon} text-neon-cyan text-2xl transition-transform duration-500 group-hover:scale-110`} />
                </div>
                <h3 className="text-[1.125rem] md:text-[1.25rem] font-semibold text-ghost group-hover:text-cyber-gold transition-colors duration-300">
                  {type.label}
                </h3>
                <div className="mt-3 flex items-center justify-center gap-1 text-cyber-gold text-base opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>探索型態</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center reveal opacity-0 translate-y-6 transition-all duration-700">
            <Link
              to="/aging-types"
              className="btn-gold-outline text-[1.0625rem]"
            >
              開始老化型態測驗 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS PREVIEW ===== */}
      <section className="py-20 md:py-28 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 reveal opacity-0 translate-y-6 transition-all duration-700">
              <span className="section-label">{testimonialsPreview.sectionLabel}</span>

              {/* Compliance note */}
              <p className="mt-4 text-ghost-dim text-[1rem] leading-[1.6] border-l-2 border-cyber-gold/20 pl-4 hover:border-cyber-gold/50 transition-colors duration-500">
                {testimonialsPreview.complianceNote}
              </p>

              <div className="mt-8 group/quote">
                <i className="ri-double-quotes-l text-cyber-gold text-3xl mb-4 block transition-transform duration-500 group-hover/quote:scale-110" />
                <div className="space-y-2">
                  {testimonialsPreview.quote.map((line, idx) => (
                    <p
                      key={idx}
                      className="text-ghost text-[1.25rem] md:text-[1.5rem] lg:text-[1.75rem] font-medium leading-[1.5] transition-all duration-500 group-hover/quote:text-cyber-gold/90"
                    >
                      {line}
                    </p>
                  ))}
                </div>
              </div>
              <div className="mt-8 flex items-center gap-4 group/author cursor-pointer">
                <div className="w-12 h-12 rounded-full bg-abyss-700 border border-cyber-gold/20 flex items-center justify-center transition-all duration-500 group-hover/author:border-cyber-gold/50 group-hover/author:scale-110">
                  <i className="ri-user-line text-ghost-muted text-xl" />
                </div>
                <div>
                  <p className="text-ghost font-medium text-[1.0625rem] group-hover/author:text-cyber-gold transition-colors duration-500">{testimonialsPreview.author}</p>
                  <div className="flex gap-1 mt-1">
                    {Array.from({ length: testimonialsPreview.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-cyber-gold text-base transition-transform duration-300 group-hover/author:scale-110" style={{ transitionDelay: `${i * 50}ms` }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <Link to="/testimonials" className="btn-gold-outline text-[1.0625rem] inline-flex items-center gap-2 group/btn">
                  更多客戶見證
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover/btn:translate-x-1" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 reveal opacity-0 translate-y-6 transition-all duration-700 group/image">
              <div className="relative">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-cyber-gold/20 transition-all duration-500 group-hover/image:border-cyber-gold/40 group-hover/image:shadow-lg group-hover/image:shadow-cyber-gold/10">
                  <img
                    src="https://readdy.ai/api/search-image?query=elegant%20confident%20mature%20woman%20portrait%20with%20subtle%20warm%20smile%2C%20dark%20studio%20background%20with%20soft%20blue%20and%20gold%20ambient%20lighting%2C%20high-end%20lifestyle%20photography%2C%20natural%20beauty%20without%20heavy%20makeup%2C%20cinematic%20quality%2C%20age%2040s&width=600&height=800&seq=testimonial-face&orientation=portrait"
                    alt="客戶見證"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover/image:scale-105"
                  />
                </div>
                <div className="absolute top-4 right-4 card-cyber px-4 py-3 transition-all duration-500 group-hover/image:-translate-y-1 group-hover/image:shadow-md">
                  <p className="font-mono text-neon-cyan text-base">INDEX</p>
                  <p className="text-ghost font-bold text-[1.25rem]">{testimonialsPreview.badge}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== EVENTS / CORPORATE BANNER ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="relative overflow-hidden rounded-2xl border border-cyber-gold/20 reveal opacity-0 translate-y-6 transition-all duration-700">
            {/* Background image with overlay */}
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20corporate%20speaker%20giving%20health%20and%20wellness%20lecture%20in%20modern%20conference%20room%20with%20dark%20blue%20ambient%20lighting%20and%20holographic%20data%20visualization%2C%20elegant%20audience%20silhouettes%2C%20futuristic%20medical%20presentation%20atmosphere%2C%20gold%20and%20cyan%20accent%20lighting%2C%20cinematic%20photography%2C%20no%20text&width=1280&height=400&seq=events-banner-bg&orientation=landscape"
                alt="活動講座與企業合作"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-abyss-900/75" />
              <div className="absolute inset-0 bg-gradient-to-r from-abyss-900/90 via-abyss-900/60 to-transparent" />
            </div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 md:p-12 lg:p-14 items-center">
              <div>
                <span className="section-label text-base">// EVENTS & COOPERATION</span>
                <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
                  讓逆齡走進企業與日常現場
                </h2>
                <p className="mt-4 text-ghost-muted text-[1.0625rem] leading-[1.75] max-w-lg">
                  曾驛翔醫師將青春痘、男性懸崖式衰老、體態健康與熟齡慢老等議題，轉化為企業、社群與機構聽得懂的語言與可行動的方案。歡迎企業內訓、異業合作與講座邀約。
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link to="/events" className="btn-gold text-[1.0625rem]">
                    了解活動與企業合作 <i className="ri-arrow-right-line" />
                  </Link>
                  <a
                    href={lineUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-line text-[1.0625rem]"
                  >
                    <i className="ri-line-fill" />
                    LINE 洽談合作
                  </a>
                </div>
              </div>
              <div className="hidden lg:flex justify-end gap-4">
                <div className="card-cyber px-5 py-4 text-center">
                  <i className="ri-mic-line text-neon-cyan text-2xl mb-2 block" />
                  <p className="text-ghost font-semibold text-base">逆齡講座</p>
                  <p className="text-ghost-dim text-base mt-1">企業 / 社群 / 學校</p>
                </div>
                <div className="card-cyber px-5 py-4 text-center">
                  <i className="ri-building-2-line text-cyber-gold text-2xl mb-2 block" />
                  <p className="text-ghost font-semibold text-base">企業內訓</p>
                  <p className="text-ghost-dim text-base mt-1">高壓工作者健康</p>
                </div>
                <div className="card-cyber px-5 py-4 text-center">
                  <i className="ri-service-line text-[#35E0B9] text-2xl mb-2 block" />
                  <p className="text-ghost font-semibold text-base">異業合作</p>
                  <p className="text-ghost-dim text-base mt-1">品牌 / 平台 / 機構</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="card-cyber p-10 md:p-16 text-center reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{finalCtaData.label}</span>
            <h2 className="mt-5 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {finalCtaData.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-line text-[1.0625rem]"
              >
                <i className="ri-line-fill" />
                {finalCtaData.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold text-[1.0625rem]">
                <i className="ri-calendar-check-line" />
                {finalCtaData.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}